import type { AuthHandoffPayload } from './auth/tokenContract';
import {
  isAllowedHost,
  isChromiumappHost,
  isLegacyHandoffEligible,
  isLegacyHost,
  labelForHost,
  legacyReplacementHost,
  PRIMARY_EXTENSION_ID,
} from './allowlist';

const SESSION_KEY = 'sf_bkit_redirect_base';
// SSO v2 (code+PKCE) params — present only for migrated clients (those
// sending ?client_id=). Persisted the same way and for the same reason as
// SESSION_KEY: Google's OAuth round trip (accounts.google.com) returns to
// this origin with a fresh query-less URL + hash fragment, so anything from
// the original query string must be stashed before redirecting to Google.
const CLIENT_ID_KEY = 'sf_bkit_client_id';
const STATE_KEY = 'sf_bkit_state';
const CODE_CHALLENGE_KEY = 'sf_bkit_code_challenge';

export function persistRedirectBase(): void {
  const params = new URLSearchParams(window.location.search);

  const redirectUri = params.get('redirect_uri');
  const clientId = params.get('client_id');
  const state = params.get('state');
  const codeChallenge = params.get('code_challenge');

  // No sign-in params at all: this is an intermediate load (e.g. the Google
  // OAuth return, which comes back with a bare URL + hash) — keep whatever
  // the original request stored.
  if (!redirectUri && !clientId) return;

  // Otherwise this is a NEW sign-in request: replace the stored set
  // atomically, clearing any key this request didn't send. Merging instead
  // (the old `if present, set` behavior) let one client's leftovers bleed
  // into the next request — e.g. a legacy-style `?redirect_uri=` from a
  // stale cached app arriving after an SSO v2 request would inherit the
  // previous client_id/state/code_challenge, producing a handoff for a
  // (client, redirect_uri) pair nobody asked for and a `state` the
  // receiving app can never validate (a guaranteed STATE_MISMATCH loop).
  if (redirectUri) sessionStorage.setItem(SESSION_KEY, redirectUri.replace(/\/$/, ''));
  const sso: Array<[string, string | null]> = [
    [CLIENT_ID_KEY, clientId],
    [STATE_KEY, state],
    [CODE_CHALLENGE_KEY, codeChallenge],
  ];
  for (const [key, value] of sso) {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  }
}

// ─── Stale cached-client bounce ──────────────────────────────────────────────
// Every migrated WEB client now sends client_id (SSO v2). A legacy-style
// request (?redirect_uri= only) from a web host therefore means the USER'S
// BROWSER is running a stale cached bundle of an app that has already
// migrated — the freshly-served app would have started code+PKCE itself.
// Best recovery: send the browser back to the requested URL once, so it
// re-fetches the app and the new bundle initiates a proper SSO request.
// The timestamp guard breaks the loop when the destination KEEPS serving the
// old bundle (aggressive CDN/proxy caching): the second legacy arrival
// within the window falls through to the 'needs-update' screen instead.
const STALE_BOUNCE_AT_KEY = 'sf_bkit_stale_bounce_at';
const STALE_BOUNCE_WINDOW_MS = 2 * 60 * 1000;

export function shouldBounceStaleClient(): boolean {
  const last = Number(sessionStorage.getItem(STALE_BOUNCE_AT_KEY) ?? 0);
  if (Date.now() - last < STALE_BOUNCE_WINDOW_MS) return false;
  sessionStorage.setItem(STALE_BOUNCE_AT_KEY, String(Date.now()));
  return true;
}

export function clearStaleClientBounce(): void {
  sessionStorage.removeItem(STALE_BOUNCE_AT_KEY);
}

export type RedirectSource = 'param' | 'session' | 'fallback';

export interface RedirectInfo {
  base: string;
  source: RedirectSource;
  label: string;
  legacy: boolean;
  // Original legacy base when `base` was rewritten to the replacement host.
  legacyBase?: string;
  allowed: boolean;
  // SSO v2 — set when the caller sent client_id: this request uses the
  // code+PKCE flow (buildCodeRedirectUrl), never the legacy fragment one.
  clientId: string | null;
  state: string | null;
  codeChallenge: string | null;
  // True when `base` resolves to a *.chromiumapp.org destination (the
  // BrowserKit extension's redirect target).
  isExtension: boolean;
  // Only meaningful when clientId is null: whether the #payload= fragment
  // handoff may still be used for this destination. False means the caller
  // needs to update — see allowlist.ts's isLegacyHandoffEligible and
  // App.tsx's 'needs-update' phase.
  legacyEligible: boolean;
}

function hostOf(base: string): string | null {
  try {
    return new URL(base).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function labelForBase(base: string): string {
  const host = hostOf(base);
  return host ? labelForHost(host) : 'the requested app';
}

function isAllowedBase(base: string): boolean {
  try {
    const url = new URL(base);
    return isAllowedHost(url.hostname.replace(/^www\./, ''), url.port);
  } catch {
    return false;
  }
}

interface SsoParams {
  clientId: string | null;
  state: string | null;
  codeChallenge: string | null;
}

// Resolves where the sign-in should hand off to, and whether we actually know
// that destination or are guessing (no ?redirect_uri was ever supplied).
function describeBase(rawBase: string, source: RedirectSource, sso: SsoParams): RedirectInfo {
  const host = hostOf(rawBase);
  const legacy = host ? isLegacyHost(host) : false;

  // Legacy host with a registered successor: hand off to the new address
  // instead, keeping the path. Always https.
  let base = rawBase;
  let legacyBase: string | undefined;
  const replacement = host ? legacyReplacementHost(host) : null;
  if (replacement) {
    try {
      const url = new URL(rawBase);
      url.hostname = replacement;
      url.protocol = 'https:';
      url.port = '';
      base = url.toString().replace(/\/$/, '');
      legacyBase = rawBase;
    } catch {
      /* keep original base */
    }
  }

  return {
    base,
    source,
    label: labelForBase(base),
    legacy,
    legacyBase,
    allowed: isAllowedBase(base),
    clientId: sso.clientId,
    state: sso.state,
    codeChallenge: sso.codeChallenge,
    isExtension: host ? isChromiumappHost(host) : false,
    legacyEligible: host ? isLegacyHandoffEligible(host) : false,
  };
}

export function getRedirectInfo(): RedirectInfo {
  const sso: SsoParams = {
    clientId: sessionStorage.getItem(CLIENT_ID_KEY),
    state: sessionStorage.getItem(STATE_KEY),
    codeChallenge: sessionStorage.getItem(CODE_CHALLENGE_KEY),
  };

  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) return describeBase(stored, 'session', sso);

  const param = new URLSearchParams(window.location.search).get('redirect_uri');
  if (param) return describeBase(param.replace(/\/$/, ''), 'param', sso);

  // Last resort: first extension ID configured in env. No real redirect_uri
  // was ever supplied, so there's no SSO v2 request to speak of either.
  return describeBase(`https://${PRIMARY_EXTENSION_ID}.chromiumapp.org`, 'fallback', {
    clientId: null,
    state: null,
    codeChallenge: null,
  });
}

// ─── Legacy handoff (#payload= fragment) ─────────────────────────────────────
// Extension-only, and only before the sunset — see allowlist.ts.

export function buildRedirectUrl(payload: AuthHandoffPayload): string {
  const info = getRedirectInfo();
  if (!info.allowed) {
    throw new Error(`Refusing to hand off credentials to unregistered destination: ${info.base}`);
  }
  if (info.clientId) {
    throw new Error(
      'buildRedirectUrl is the legacy path — this request carries client_id and must use buildCodeRedirectUrl instead.',
    );
  }
  if (!info.legacyEligible) {
    throw new Error(
      `Refusing legacy handoff to ${info.base} — not eligible (extension-only, and/or past the configured sunset). See allowlist.ts.`,
    );
  }

  const encoded = btoa(
    encodeURIComponent(JSON.stringify(payload)).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16)),
    ),
  )
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return `${info.base}/#payload=${encoded}`;
}

// ─── SSO v2 handoff (authorization code + PKCE) ──────────────────────────────

const SSO_API_URL = ((import.meta.env.VITE_SSO_API_URL as string) || 'https://api.sellerflow.com.br').replace(
  /\/+$/,
  '',
);

export class SsoHandoffError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'SsoHandoffError';
    this.code = code;
  }
}

// Exchanges a freshly-minted Firebase ID token for a single-use code at
// SF-DataFlow-API, then builds the `redirect_uri?code=&state=` the caller
// redeems at POST /auth/sso/token with its own stored PKCE verifier. No
// token — Firebase's or ours — ever touches this URL or this browser's
// storage beyond the current call stack. See design doc SSO v2 §4.1-4.2.
export async function buildCodeRedirectUrl(firebaseIdToken: string): Promise<string> {
  const info = getRedirectInfo();
  if (!info.clientId || !info.codeChallenge) {
    throw new Error(
      'buildCodeRedirectUrl requires client_id and code_challenge — this request has neither, use buildRedirectUrl instead.',
    );
  }

  const res = await fetch(`${SSO_API_URL}/auth/sso/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      firebase_id_token: firebaseIdToken,
      client_id: info.clientId,
      redirect_uri: info.base,
      code_challenge: info.codeChallenge,
    }),
  });

  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    const detail = (body as { detail?: { code?: string; message?: string } }).detail ?? {};
    throw new SsoHandoffError(detail.code ?? 'UNKNOWN', detail.message ?? `SSO handoff failed (HTTP ${res.status})`);
  }

  const code = (body as { code: string }).code;
  const params = new URLSearchParams({ code });
  if (info.state) params.set('state', info.state);

  const separator = info.base.includes('?') ? '&' : '?';
  return `${info.base}${separator}${params.toString()}`;
}
